import { ShieldCheck } from "lucide-react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

/** A tag beside a name: "External" (another enterprise's people) in violet, anything else neutral, such as "Sample". */
export function Tag({
  label,
  tone = "neutral",
  className,
}: {
  label: string;
  tone?: "neutral" | "external";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-4.5 shrink-0 items-center rounded-(--radius-chip) px-1.5 text-tag font-semibold whitespace-nowrap",
        tone === "external"
          ? "bg-tag-external-bg text-tag-external-fg"
          : "bg-tone-neutral-bg text-tone-neutral-fg",
        className,
      )}
    >
      {label}
    </span>
  );
}

/**
 * A count in the accent, such as unread messages, 18 px high; above `max` it shows "99+". `label` says what is counted
 * for screen readers ("3 unread"), since the figure alone does not. `quiet` greys it, as for a muted conversation.
 */
export function Count({
  value,
  label,
  max = 99,
  quiet = false,
}: {
  value: number;
  label?: string;
  max?: number;
  quiet?: boolean;
}) {
  const figure = value > max ? `${max}+` : `${value}`;
  return (
    <span
      className={cn(
        "inline-flex h-4.5 min-w-4.5 shrink-0 items-center justify-center rounded-full px-1.5 text-tag font-semibold tabular-nums",
        quiet ? "bg-muted text-ink-body" : "bg-brand-600 text-on-brand",
      )}
    >
      {label === undefined ? (
        figure
      ) : (
        <>
          <span aria-hidden="true">{figure}</span>
          <span className="sr-only">{label}</span>
        </>
      )}
    </span>
  );
}

/** "Verified business": an enterprise that passed verification, in the done tone with a shield. */
export function VerifiedMark({ label, className }: { label: string; className?: string }) {
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-caption font-medium text-tone-done-fg", className)}
    >
      <Icon icon={ShieldCheck} size={16} className="size-3.5" />
      {label}
    </span>
  );
}
